import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as adminApi from '../../services/admin';
import SalesChart from '../../components/admin/SalesChart';
import '../admin/ManagementPages.css';
import './ReceptionistDashboard.css';

/**
 * Receptionist Dashboard
 * Displays bookings, sales, and other metrics relevant to receptionist role
 */
const ReceptionistDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    completedBookings: 0,
    totalSales: 0,
    totalRooms: 0,
    availableRooms: 0,
    occupiedRooms: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // Set up interval to refresh daily sales every 5 minutes
    const refreshInterval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [bookingsRes, roomsRes, salesRes] = await Promise.all([
        adminApi.getAllBookings().catch(() => []),
        adminApi.getAllRooms().catch(() => []),
        adminApi.getAllSales().catch(() => []),
      ]);

      // Backend returns arrays directly, not nested objects
      const bookings = Array.isArray(bookingsRes) ? bookingsRes : [];
      const rooms = Array.isArray(roomsRes) ? roomsRes : [];
      const sales = Array.isArray(salesRes) ? salesRes : [];

      // Calculate stats
      const pendingCount = bookings.filter(b => b.status === 'Pending').length;
      const confirmedCount = bookings.filter(b => b.status === 'Confirmed').length;
      const completedCount = bookings.filter(b => b.status === 'Completed').length;
      const availableCount = rooms.filter(r => r.status === 'Available').length;
      const occupiedCount = rooms.filter(r => r.status === 'Occupied').length;
      const totalSales = sales.reduce((sum, s) => sum + (s.totalAmount || s.amount || 0), 0);

      setStats({
        totalBookings: bookings.length,
        pendingBookings: pendingCount,
        confirmedBookings: confirmedCount,
        completedBookings: completedCount,
        totalSales: totalSales,
        totalRooms: rooms.length,
        availableRooms: availableCount,
        occupiedRooms: occupiedCount,
      });



    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="management-page receptionist-dashboard">
      <div className="page-header">
        <div className="header-content">
          <h1>Receptionist Dashboard</h1>
          <p>Overview of bookings, sales, and room management</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {/* Bookings Stats */}
        <div className="stat-card">
          <div className="stat-icon bookings">
            <i className="fas fa-calendar-alt"></i>
          </div>
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <p className="stat-number">{stats.totalBookings}</p>
            <div className="stat-breakdown">
              <span className="pending">Pending: {stats.pendingBookings}</span>
              <span className="confirmed">Confirmed: {stats.confirmedBookings}</span>
              <span className="completed">Completed: {stats.completedBookings}</span>
            </div>
          </div>
        </div>

        {/* Rooms Stats */}
        <div className="stat-card">
          <div className="stat-icon rooms">
            <i className="fas fa-bed"></i>
          </div>
          <div className="stat-content">
            <h3>Rooms</h3>
            <p className="stat-number">{stats.totalRooms}</p>
            <div className="stat-breakdown">
              <span className="available">Available: {stats.availableRooms}</span>
              <span className="occupied">Occupied: {stats.occupiedRooms}</span>
            </div>
          </div>
        </div>

        {/* Sales Stats */}
        <div className="stat-card">
          <div className="stat-icon sales">
            <i className="fas fa-chart-simple"></i>
          </div>
          <div className="stat-content">
            <h3>Total Sales</h3>
            <p className="stat-number">₱{stats.totalSales.toFixed(2)}</p>
            <span className="stat-label">All time sales</span>
          </div>
        </div>
      </div>

      {/* Sales and booking graphs, matching the Admin Dashboard */}
      <SalesChart />

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <button 
            className="action-btn"
            onClick={() => navigate('/receptionist/bookings')}
          >
            <i className="fas fa-calendar-check"></i>
            <span>Manage Bookings</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => navigate('/receptionist/rooms')}
          >
            <i className="fas fa-door-open"></i>
            <span>View Rooms</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => navigate('/receptionist/inventory')}
          >
            <i className="fas fa-boxes"></i>
            <span>Check Inventory</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => navigate('/receptionist/sales')}
          >
            <i className="fas fa-chart-bar"></i>
            <span>View Sales</span>
          </button>
          <button 
            className="action-btn"
            onClick={() => navigate('/receptionist/reports')}
          >
            <i className="fas fa-file-pdf"></i>
            <span>Generate Reports</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceptionistDashboard;
